import { useEffect, useState } from "react";
import { useSearchParams, Link, Navigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import VillaCard from "@/components/villa/VillaCard";
import { format } from "date-fns";
import ConciergeDrawer from "@/components/concierge/ConciergeDrawer";
import ReviewDialog from "@/components/review/ReviewDialog";
import Eyebrow from "@/components/common/Eyebrow";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import { alpha } from "@mui/material/styles";

function TabPanel({ value, current, children }) {
  if (value !== current) return null;
  return <Box>{children}</Box>;
}

export default function Dashboard() {
  const { user, logout, loading: authLoading } = useAuth();
  const [params] = useSearchParams();
  const initTab = params.get("tab") || "trips";
  const [tab, setTab] = useState(initTab);
  const [bookings, setBookings] = useState([]);
  const [saved, setSaved] = useState([]);
  const [reviewedBookingIds, setReviewedBookingIds] = useState(new Set());

  const loadReviews = () => {
    api.get("/reviews/mine").then(r => setReviewedBookingIds(new Set(r.data.map(x => x.booking_id)))).catch(()=>{});
  };

  useEffect(() => {
    if (!user) return;
    api.get("/bookings").then(r => setBookings(r.data)).catch(()=>{});
    api.get("/me/saved").then(r => setSaved(r.data)).catch(()=>{});
    loadReviews();
  }, [user]);

  if (authLoading) {
    return (
      <Box sx={{ py: 16, textAlign: "center", fontSize: "0.875rem", color: "text.secondary" }}>
        Loading…
      </Box>
    );
  }
  if (!user) {
    return (
      <Box sx={{ py: 16, textAlign: "center" }}>
        Please{" "}
        <Link to="/login" style={{ color: "var(--mui-palette-primary-main, #b8860b)" }}>
          sign in
        </Link>
        .
      </Box>
    );
  }
  if (user.role === "admin") {
    return <Navigate to="/admin" replace />;
  }
  if (user.role === "owner") {
    return <Navigate to="/owner" replace />;
  }

  return (
    <Box sx={{ maxWidth: 1400, mx: "auto", px: { xs: 3, lg: 6 }, py: 6 }} data-testid="dashboard-page">
      <Box sx={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", mb: 5, flexWrap: "wrap", gap: 2 }}>
        <Box>
          <Eyebrow sx={{ mb: 1 }}>Welcome back</Eyebrow>
          <Typography
            component="h1"
            sx={{
              fontFamily: "'Playfair Display', serif",
              fontSize: { xs: "2.25rem", md: "3rem" },
              lineHeight: 1.1,
            }}
          >
            {user.name}
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mt: 1 }}>
            {user.email}
          </Typography>
        </Box>
        <Button onClick={logout} variant="outlined" size="small" data-testid="logout-btn">
          Sign out
        </Button>
      </Box>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ mb: 4, minHeight: 0, "& .MuiTabs-indicator": { bgcolor: "primary.main" } }}
      >
        <Tab value="trips" label="Trips" data-testid="tab-trips" />
        <Tab value="saved" label="Saved" data-testid="tab-saved" />
        <Tab value="wallet" label="Wallet" data-testid="tab-wallet" />
        <Tab value="profile" label="Profile" data-testid="tab-profile" />
      </Tabs>

      <TabPanel value="trips" current={tab}>
        {bookings.length === 0 ? (
          <Box sx={{ py: 10, textAlign: "center" }}>
            <Typography sx={{ fontFamily: "'Playfair Display', serif", fontSize: "1.5rem", mb: 1.5 }}>
              No trips yet
            </Typography>
            <Link to="/villas" style={{ color: "var(--mui-palette-primary-main, #b8860b)", fontSize: "0.875rem" }}>
              Explore villas →
            </Link>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {bookings.map(b => (
              <Box
                key={b.id}
                data-testid={`booking-${b.id}`}
                sx={{
                  display: "flex",
                  gap: 2.5,
                  p: 2,
                  borderRadius: 4,
                  border: 0,
                  boxShadow: 1,
                  transition: "box-shadow 0.2s ease",
                  "&:hover": { boxShadow: 3 },
                }}
              >
                <Box
                  component="img"
                  src={b.villa_image}
                  alt=""
                  sx={{
                    width: 128,
                    height: 96,
                    borderRadius: 3,
                    objectFit: "cover",
                    flexShrink: 0,
                  }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontFamily: "'Playfair Display', serif", fontSize: "1.25rem" }}>
                    {b.villa_name}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
                    {format(new Date(b.check_in), "dd MMM")} – {format(new Date(b.check_out), "dd MMM yyyy")} · {b.guests} guests
                  </Typography>
                  <Box
                    sx={{
                      fontSize: 10,
                      mt: 1,
                      display: "inline-block",
                      px: 1.25,
                      py: 0.5,
                      borderRadius: 999,
                      bgcolor: (t) => alpha(t.palette.primary.main, 0.1),
                      color: "primary.main",
                      textTransform: "uppercase",
                      letterSpacing: "0.1em",
                    }}
                  >
                    {b.status}
                  </Box>
                </Box>
                <Box sx={{ textAlign: "right", display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 1 }}>
                  <Box>
                    <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
                      Total
                    </Typography>
                    <Typography sx={{ fontWeight: 500 }}>₹{b.total.toLocaleString()}</Typography>
                  </Box>
                  <ConciergeDrawer booking={b} />
                  {new Date(b.check_out) < new Date() && !reviewedBookingIds.has(b.id) && (
                    <ReviewDialog booking={b} onSubmitted={loadReviews} />
                  )}
                  {reviewedBookingIds.has(b.id) && (
                    <Typography
                      component="span"
                      data-testid={`reviewed-${b.id}`}
                      sx={{
                        fontSize: 10,
                        textTransform: "uppercase",
                        letterSpacing: "0.1em",
                        color: "primary.main",
                      }}
                    >
                      ✓ Reviewed
                    </Typography>
                  )}
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </TabPanel>

      <TabPanel value="saved" current={tab}>
        {saved.length === 0 ? (
          <Box sx={{ py: 10, textAlign: "center" }}>
            <Typography sx={{ fontFamily: "'Playfair Display', serif", fontSize: "1.5rem", mb: 1.5 }}>
              No saved villas yet
            </Typography>
            <Link to="/villas" style={{ color: "var(--mui-palette-primary-main, #b8860b)", fontSize: "0.875rem" }}>
              Start exploring →
            </Link>
          </Box>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "repeat(3, 1fr)" },
              gap: 3,
            }}
          >
            {saved.map(v => <VillaCard key={v.id} villa={v} />)}
          </Box>
        )}
      </TabPanel>

      <TabPanel value="wallet" current={tab}>
        <Box sx={{ borderRadius: 4, border: 0, boxShadow: 1, p: 4, maxWidth: 420 }}>
          <Eyebrow sx={{ mb: 1.5 }}>Available credits</Eyebrow>
          <Typography sx={{ fontFamily: "'Playfair Display', serif", fontSize: "2.25rem" }}>₹0</Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mt: 1.5 }}>
            Earn credits with every stay. Refer friends to unlock instant bonuses.
          </Typography>
        </Box>
      </TabPanel>

      <TabPanel value="profile" current={tab}>
        <Box sx={{ maxWidth: 420, display: "flex", flexDirection: "column", gap: 2 }}>
          <Box sx={{ borderRadius: 3, border: 0, boxShadow: 1, p: 2.5 }}>
            <Eyebrow sx={{ fontSize: "0.5625rem", mb: 0.5 }}>Name</Eyebrow>
            <Typography variant="body2">{user.name}</Typography>
          </Box>
          <Box sx={{ borderRadius: 3, border: 0, boxShadow: 1, p: 2.5 }}>
            <Eyebrow sx={{ fontSize: "0.5625rem", mb: 0.5 }}>Email</Eyebrow>
            <Typography variant="body2">{user.email}</Typography>
          </Box>
          <Box sx={{ borderRadius: 3, border: 0, boxShadow: 1, p: 2.5 }}>
            <Eyebrow sx={{ fontSize: "0.5625rem", mb: 0.5 }}>Account type</Eyebrow>
            <Typography variant="body2" sx={{ textTransform: "capitalize" }}>{user.role}</Typography>
          </Box>
        </Box>
      </TabPanel>
    </Box>
  );
}
